import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, MapPin, Clock, Shield, Zap, ChevronDown } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { PHONES, CONTACT } from '../content';
import { getNeighborhoodBySlug } from '../data/neighborhoods';
import { SERVICE_PAGES } from '../data/services-seo';
import { SEOHead, BreadcrumbSchema } from '../components/SEOHead';
import Breadcrumb from '../components/Breadcrumb';
import FinalCTA from '../components/FinalCTA';
import LiveBadge from '../components/LiveBadge';

const WA_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M5.077 18.828l.532-1.869A8.85 8.85 0 0 1 3.2 12C3.2 7.134 7.134 3.2 12 3.2c4.866 0 8.8 3.934 8.8 8.8 0 4.866-3.934 8.8-8.8 8.8a8.845 8.845 0 0 1-4.893-1.471l-2.03.499zM12 1C6.477 1 2 5.477 2 11c0 1.821.439 3.538 1.215 5.057L2 22l6.071-1.59A10.976 10.976 0 0 0 12 21c5.523 0 10-4.477 10-10S17.523 1 12 1z"/>
  </svg>
);

const TRUST = [
  { icon: Zap, value: '15 dk', label: 'Müdahale', color: 'var(--red)' },
  { icon: Shield, value: '24 Ay', label: 'Garanti', color: 'var(--green)' },
  { icon: Clock, value: '7/24', label: 'Aktif', color: '#ff9500' },
];

export default function NeighborhoodPage() {
  const location = useLocation();
  const slug = location.pathname.replace('/', '');
  const hood = getNeighborhoodBySlug(slug);

  if (!hood) return null;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": hood.faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  return (
    <main data-testid={`neighborhood-page-${hood.slug}`} style={{ background: 'var(--bg)' }}>
      <SEOHead path={`/${hood.slug}`} title={hood.title} description={hood.metaDesc} />
      <BreadcrumbSchema items={[
        { name: 'Anasayfa', url: '/' },
        { name: `${hood.district} Lastik Yol Yardım`, url: `/${hood.districtSlug}` },
        { name: hood.name },
      ]} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* HERO */}
      <section style={{ background: 'var(--bg)', paddingTop: 86, borderBottom: '1px solid var(--border)' }}>
        <div className="wrap" style={{ padding: '3rem 1.5rem 2.5rem' }}>
          <Breadcrumb items={[
            { label: 'Anasayfa', to: '/' },
            { label: `${hood.district} Lastik Yol Yardım`, to: `/${hood.districtSlug}` },
            { label: hood.name },
          ]} />

          <LiveBadge text={`${hood.name} — Ekip Aktif`} />

          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .5 }}
            style={{
              fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800,
              fontSize: 'clamp(1.75rem, 5vw, 3rem)', color: 'var(--text)',
              lineHeight: 1.08, letterSpacing: '-.04em', margin: '1rem 0 .5rem',
            }}
          >
            {hood.name} Lastikçi
          </motion.h1>
          <p style={{
            fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800,
            fontSize: 'clamp(1.1rem, 3vw, 1.75rem)', color: 'var(--red)',
            lineHeight: 1.1, letterSpacing: '-.03em', marginBottom: '1rem',
          }}>
            {hood.district} — 7/24 Yerinde Hizmet
          </p>
          <p style={{ fontSize: 'clamp(.875rem, 1.5vw, 1rem)', color: 'var(--text2)', lineHeight: 1.7, maxWidth: 560 }}>
            {hood.intro}
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.625rem', marginTop: '1.5rem' }}>
            <a href={PHONES.main_tel} data-testid="hood-call-cta" className="btn btn-primary num ring" style={{ gap: '.5rem' }}>
              <Phone size={15} /> {PHONES.main}
            </a>
            <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer"
              data-gtm-action="whatsapp_click" data-testid="hood-whatsapp-cta"
              className="btn btn-green" style={{ gap: '.4rem', fontSize: '.875rem' }}>
              {WA_ICON} WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <div className="wrap">
          <div style={{ display: 'flex', justifyContent: 'center', gap: '2.5rem', padding: '1.25rem 0', flexWrap: 'wrap' }}>
            {TRUST.map(({ icon: Icon, value, label, color }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                <Icon size={16} style={{ color }} />
                <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800, fontSize: '1rem', color: 'var(--text)' }}>{value}</span>
                <span style={{ fontSize: '.75rem', color: 'var(--text2)' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {hood.faqs.length > 0 && (
        <section className="section" style={{ background: 'var(--bg)' }}>
          <div className="wrap">
            <h2 className="h2" style={{ marginBottom: '1.5rem' }}>
              Sık Sorulan <span style={{ color: 'var(--blue)' }}>Sorular</span>
            </h2>
            <div style={{ maxWidth: 700, display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
              {hood.faqs.map((faq, i) => (
                <FAQItem key={faq.q} faq={faq} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SERVICES */}
      <section className="section" style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
        <div className="wrap">
          <h2 className="h2" style={{ marginBottom: '1.25rem' }}>
            {hood.name} Bölgesinde <span style={{ color: 'var(--green)' }}>Hizmetlerimiz</span>
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
            {SERVICE_PAGES.map(s => (
              <Link key={s.slug} to={`/${s.slug}`} style={{
                display: 'inline-block', background: 'var(--card)', border: '1px solid var(--border)',
                borderRadius: 100, padding: '.35rem .9rem', fontSize: '.8rem', color: 'var(--text2)',
                textDecoration: 'none', transition: 'border-color .2s, color .2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.color = 'var(--green)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; }}
              >{s.h1}</Link>
            ))}
          </div>
          <div style={{ marginTop: '1rem' }}>
            <Link to={`/${hood.districtSlug}`} style={{ fontSize: '.85rem', color: 'var(--blue)', fontWeight: 600 }}>
              ← {hood.district} Lastik Yol Yardım sayfasına dön
            </Link>
          </div>
        </div>
      </section>

      <FinalCTA headline={`${hood.name}'de Yolda Kalma.`} sub="Hemen Ara." />
    </main>
  );
}

function FAQItem({ faq, index }) {
  const [isOpen, setIsOpen] = React.useState(false);
  return (
    <div
      data-testid={`hood-faq-${index}`}
      style={{
        background: 'var(--card)', border: `1px solid ${isOpen ? 'var(--blue-border)' : 'var(--border)'}`,
        borderRadius: 'var(--r)', overflow: 'hidden', transition: 'border-color .25s', cursor: 'pointer',
      }}
      onClick={() => setIsOpen(!isOpen)}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', gap: '.75rem' }}>
        <h3 style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '.88rem', color: 'var(--text)', lineHeight: 1.4, margin: 0 }}>
          {faq.q}
        </h3>
        <ChevronDown size={16} style={{ flexShrink: 0, color: 'var(--muted)', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .25s' }} />
      </div>
      {isOpen && (
        <div style={{ padding: '0 1.25rem 1rem', fontSize: '.84rem', color: 'var(--text2)', lineHeight: 1.75 }}>
          {faq.a}
        </div>
      )}
    </div>
  );
}
